import CloseIcon from '@material-ui/icons/ClearOutlined'
import { ToolExecutionState } from 'ducks/chatDrawer'
import {
  AlertCircleOutlineIcon,
  CheckOutlineIcon,
  IconButton,
  keyframes,
  styled,
  Tooltip,
  Typography,
} from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import React from 'react'
import { useDispatch } from 'react-redux'
import { useCountdownTimeout } from '../../hooks/useCountdownTimeout'

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-4px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

interface AutoStringProgressItemProps {
  toolExecutionState: ToolExecutionState
  messageIndex: number
}

const Container = styled('div')<{ isAutoAccepted?: boolean }>(({ isAutoAccepted }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '10px 12px',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  borderRadius: '10px',
  marginTop: '12px',
  border: isAutoAccepted ? '2px solid #1751D0' : '1px solid rgba(23, 81, 208, 0.12)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
  position: 'relative',
  overflow: 'hidden',
  animation: `${fadeIn} 0.3s ease-out`,
}))

const StatusRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  position: 'relative',
  zIndex: 1,
})

const Description = styled(Typography)({
  flex: 1,
  fontSize: '13px',
  lineHeight: '18px',
})

const Spinner = styled('div')({
  width: '24px',
  height: '24px',
  border: '3px solid #f3f3f3',
  borderTop: '3px solid #3984d8',
  borderRadius: '50%',
  animation: `${spin} 1s linear infinite`,
})

const CancelButton = styled(IconButton)({
  width: '24px',
  height: '24px',
  padding: '4px',
  color: '#9CA3AF',
  transition: 'color 200ms ease, background-color 200ms ease',
  '&:hover': {
    color: '#6B7280',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  '& svg': {
    width: '16px',
    height: '16px',
  },
})

const IconWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  borderRadius: '6px',
  flexShrink: 0,
})

const SuccessIconWrapper = styled(IconWrapper)({
  background: 'linear-gradient(135deg, rgba(1, 102, 38, 0.1) 0%, rgba(1, 144, 48, 0.1) 100%)',
})

const ErrorIconWrapper = styled(IconWrapper)({
  background: 'linear-gradient(135deg, rgba(188, 28, 28, 0.1) 0%, rgba(225, 33, 33, 0.1) 100%)',
})

const CountdownBarContainer = styled('div')({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '3px',
  borderRadius: '0 0 10px 10px',
  overflow: 'hidden',
  zIndex: 10,
})

const shrinkKeyframes = keyframes`
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
`

const CountdownBarCompleted = styled('div')<{ duration: number }>(({ duration }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: '#016626',
  transformOrigin: 'left',
  animation: `${shrinkKeyframes} ${duration}s linear forwards`,
}))

const CountdownBarError = styled('div')<{ duration: number }>(({ duration }) => ({
  height: '100%',
  width: '100%',
  backgroundColor: '#BC1C1C',
  transformOrigin: 'left',
  animation: `${shrinkKeyframes} ${duration}s linear forwards`,
}))

const AutoAcceptedWrapper: React.FC<{ isAutoAccepted: boolean; children: React.ReactElement }> = ({
  isAutoAccepted,
  children,
}) => {
  const translate = useTranslate()
  if (isAutoAccepted) {
    return (
      <Tooltip title={translate('This action was auto-accepted')} placement="top">
        <span>{children}</span>
      </Tooltip>
    )
  }
  return children
}

export const AutoStringProgressItem: React.FC<AutoStringProgressItemProps> = ({ toolExecutionState, messageIndex }) => {
  const dispatch = useDispatch()
  const translate = useTranslate()
  const isAutoAccepted = toolExecutionState.isAutoAccepted || false

  // Handle countdown completion with CSS animation
  useCountdownTimeout(toolExecutionState.status, { completed: 5, error: 15 }, dispatch, messageIndex)

  const handleCancel = () => {
    // Clear the tool execution state to dismiss the component
    dispatch({
      type: 'UPDATE_TOOL_EXECUTION_STATE',
      messageIndex,
      toolExecutionState: undefined as any,
    })
  }

  // Helper to render countdown bar
  const renderCountdownBar = (
    duration: number,
    backgroundColor: string,
    BarComponent: typeof CountdownBarCompleted | typeof CountdownBarError
  ) => {
    return (
      <CountdownBarContainer style={{ backgroundColor }}>
        <BarComponent duration={duration} />
      </CountdownBarContainer>
    )
  }

  // Render status row content based on status
  const renderStatusContent = () => {
    switch (toolExecutionState.status) {
      case 'running':
        return (
          <>
            <Spinner />
            <Description textVariant="body1" style={{ color: '#666' }}>
              {translate('Running auto string...')}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )

      case 'completed':
        return (
          <>
            <SuccessIconWrapper>
              <CheckOutlineIcon size={20} color="#016626" />
            </SuccessIconWrapper>
            <Description
              textVariant="body1"
              style={{
                color: '#016626',
                fontWeight: 500,
              }}
            >
              {translate('Auto stringing complete')}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )

      case 'error': {
        const errorMessage =
          toolExecutionState.error?.message ||
          toolExecutionState.description ||
          toolExecutionState.error?.errorDetails ||
          translate('Auto stringing failed')
        return (
          <>
            <ErrorIconWrapper>
              <AlertCircleOutlineIcon size={20} color="#BC1C1C" />
            </ErrorIconWrapper>
            <Description
              textVariant="caption1"
              style={{
                color: '#BC1C1C',
                fontWeight: 500,
              }}
            >
              {errorMessage}
            </Description>
            <CancelButton
              size="small"
              onClick={handleCancel}
              aria-label={translate('Cancel')}
              title={translate('Cancel')}
            >
              <CloseIcon />
            </CancelButton>
          </>
        )
      }

      default:
        return null
    }
  }

  const statusContent = renderStatusContent()
  if (!statusContent) return null

  return (
    <AutoAcceptedWrapper isAutoAccepted={isAutoAccepted}>
      <Container isAutoAccepted={isAutoAccepted}>
        <StatusRow>{statusContent}</StatusRow>
        {toolExecutionState.status === 'completed' &&
          renderCountdownBar(5, 'rgba(1, 102, 38, 0.1)', CountdownBarCompleted)}
        {toolExecutionState.status === 'error' && renderCountdownBar(15, 'rgba(188, 28, 28, 0.1)', CountdownBarError)}
      </Container>
    </AutoAcceptedWrapper>
  )
}
